<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { computed } from "vue";
import { AtomText } from "@chatfood/design-system";
import { BasketItems } from "../basket-items";
import { PriceBreakdown } from "../price-breakdown";
import type { IBasketSummaryProps } from "./props";
import { useBasketStore } from "~/stores/basket";
import { useOutletStore } from "~/stores/outlet";
import { useLocale } from "~/composables/use-locale";

const props = defineProps<IBasketSummaryProps>();

const css = bemBuilder("basket-summary");

const basket = useBasketStore();
const outlet = useOutletStore();

const { t } = useLocale();

const basketTotal = computed(() => {
  if (!props.tipAmount) return basket.total;

  return {
    currency: basket.total.currency,
    value: basket.total.value + props.tipAmount.value,
  };
});

const getGiftCardPayment = computed(() => {
  const payment = basket.payments.find(
    (payment) => payment.type === "gift_card",
  );

  return payment?.amount;
});
</script>

<template>
  <div :class="css('')">
    <BasketItems :class="css('line')" />
    <div v-if="basket.notes" :class="css('order-notes')" data-test="order-note">
      <AtomText color="color-text-disabled" type="body-bold">
        {{ t("components.basket_summary.order_note") }}
      </AtomText>
      <AtomText :class="css('notes')" color="color-text-disabled" type="body">
        {{ basket.notes }}
      </AtomText>
    </div>
    <PriceBreakdown
      :class="css('line')"
      :discounted-amount="basket.totalBreakdown.discountedAmount"
      :delivery-fee="basket.totalBreakdown.deliveryFee"
      :products-total="basket.totalBreakdown.basketTotal"
      :loyalty-redeemed="basket.totalBreakdown.loyaltyRedeemed"
      :tax-amount="basket.totalBreakdown.taxAmount"
      :service-charges="basket.totalBreakdown.serviceCharges"
      :bag-fee="basket.totalBreakdown.bagFee"
      :subtotal="basket.totalBreakdown.subtotal"
      :tip-amount="props.tipAmount"
      :total="basketTotal"
      :gift-card="getGiftCardPayment"
      :outstanding-balance="basket.totalBreakdown.outstandingBalance"
      :is-tax-included="outlet.isTaxIncluded"
    />
  </div>
</template>

<style lang="scss" scoped>
.basket-summary {
  &__line {
    padding: var(--spacing-md) 0;
    border-bottom: 1px solid var(--color-base-95);

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
      border: none;
    }
  }

  &__order-notes {
    padding: var(--spacing-md) 0;
    border-bottom: 1px solid var(--color-base-95);
  }

  &__notes {
    white-space: break-spaces;
    margin-left: var(--spacing-xs);
  }
}
</style>
